// src/skills/glyphbinding/glyphbindingUI.js
// UI rendering for the Glyphbinding skill - Gatherer-style card layout

import { gameState } from '../../game/gameState.js';
import { SKILLS } from '../../game/constants.js';
import { glyphbindingSkill } from './glyphbinding.js';
import { ESSENCE_NODES, GLYPH_RECIPES, GLYPH_CORE_RECIPES, FOCUS_RECIPES, CHISELS } from './glyphbindingData.js';
import { getItemName, getItemIcon } from '../../data/items/index.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { updateToolUpgradeCost } from '../../ui/uiHelpers.js';
import { formatNumber, formatWithCommas } from '../../utils/format.js';
import logger from '../../utils/logger.js';
import { getCorruptionSpeedMultiplier, getCorruptionLootMultiplier, getCorruptionXPMultiplier } from '../../systems/corruption.js';
import { getXPPerHour } from '../../systems/experience.js';
import { getSkillProgressInfo, updateCompactHeaderProgress } from '../../ui/skillProgress.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';
import { getTalentXPBonus, getTalentLootBonus, getTalentFlatBonus } from '../../systems/talentTree/talentTree.js';
import { buildEquipmentStatsTooltip } from '../../ui/equipmentTooltip.js';
import { getSkillIcon } from '../../core/skillRegistry.js';
import { getAtlasBgStyle } from '../../data/atlasManifest.js';

let currentTab = 'ichor';

// Track selected ichor override per glyph/core/focus (when catalyst active)
// Key: recipeId, Value: ichor item ID
const selectedIchorOverrides = {};

// All ichor types for catalyst substitution
const ALL_ICHOR_TYPES = ['arcane_residue', 'refined_arcane_ichor', 'purified_arcane_core'];

// Track catalyst state to detect changes (banking/withdrawing catalyst)
let lastKnownCatalystState = null;

// Helper to check if an item is an ichor type
function isIchorItem(itemId) {
    return ALL_ICHOR_TYPES.includes(itemId);
}

// Fire icon sprite for ashes bonus (lazy — atlas loads after module import)
let _fireSpriteCache = null;
function getFireSpriteHTML() {
    if (_fireSpriteCache) return _fireSpriteCache;
    const sheet = 'assets/icons/ui_icons/fire.png';
    const atlas = getAtlasBgStyle(sheet, 16, 16);
    _fireSpriteCache = atlas
        ? `<span class="sprite-icon fire-sprite" style="${atlas.style}"></span>`
        : `<span class="sprite-icon fire-sprite" style="background-image: url('${sheet}'); background-position: 0px 0px; width: 16px; height: 16px; background-size: contain;"></span>`;
    return _fireSpriteCache;
}

// ============================================
//   COMPACT HEADER
// ============================================

function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
    const xpPerHour = getXPPerHour(SKILLS.GLYPHBINDING);
    const { levelDisplay, progressPercent, progressText } = getSkillProgressInfo(SKILLS.GLYPHBINDING);
    const currentAction = gameState.getCurrentAction();
    const isGlyphbindingAction = currentAction?.type === SKILLS.GLYPHBINDING;
    const actionProgress = isGlyphbindingAction ? currentAction.progress : 0;

    // Tool info
    const activeBuff = glyphbindingSkill.getActiveBuff();
    const chisels = Object.values(CHISELS);
    const currentIndex = chisels.findIndex(c => c.id === activeBuff.id);
    const nextChisel = chisels[currentIndex + 1];

    // Corruption multipliers
    const corruptionSpeed = getCorruptionSpeedMultiplier(SKILLS.GLYPHBINDING);
    const corruptionLoot = getCorruptionLootMultiplier(SKILLS.GLYPHBINDING);
    const corruptionXP = getCorruptionXPMultiplier(SKILLS.GLYPHBINDING);

    let upgradeHtml = '';
    if (nextChisel) {
        const canAfford = gameState.getGold() >= nextChisel.price;
        const meetsLevel = skill.level >= nextChisel.level;
        const isLocked = !meetsLevel;

        if (isLocked) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    → ${nextChisel.name} (${nextChisel.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextChisel.price}">${getItemIcon('coins')} ${formatNumber(nextChisel.price)}</span>
                    <span class="upgrade-lock">🔒 ${skill.level}/${nextChisel.level}</span>
                </span>`;
        } else {
            upgradeHtml = `
                <span class="compact-upgrade available" onclick="window.glyphbindingUI.upgradeChisel('${nextChisel.id}')">
                    Upgrade: ${nextChisel.name}
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextChisel.price}">${getItemIcon('coins')} ${formatNumber(nextChisel.price)}</span>
                </span>`;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">Max Chisel</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.GLYPHBINDING)}</span>
                    <span class="compact-name">Glyphbinding</span>
                    ${renderSkillInfo(SKILLS.GLYPHBINDING)}
                    ${renderTalentButton(SKILLS.GLYPHBINDING)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                </div>
                <div class="compact-stats">
                    <div class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </div>
                    <div class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </div>
                    ${corruptionXP > 1 ? `
                    <div class="compact-stat corruption-xp">
                        <span class="stat-label">XP:</span>
                        <span class="stat-value">${corruptionXP.toFixed(1)}x</span>
                    </div>` : ''}
                    ${corruptionSpeed > 1 ? `
                    <div class="compact-stat corruption-speed">
                        <span class="stat-label">Speed:</span>
                        <span class="stat-value">${corruptionSpeed.toFixed(1)}x</span>
                    </div>` : ''}
                    ${corruptionLoot > 1 ? `
                    <div class="compact-stat corruption-loot">
                        <span class="stat-label">Loot:</span>
                        <span class="stat-value">${corruptionLoot.toFixed(1)}x</span>
                    </div>` : ''}
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Chisel:</span>
                    <span class="tool-current">${activeBuff.name}</span>
                    <span class="tool-speed">(${activeBuff.speedMultiplier}x)</span>
                </div>
                ${upgradeHtml}
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${progressPercent}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

// ============================================
//   TABS
// ============================================

function renderTabs() {
    // Use flame glyph as silhouette icon for Glyphs tab
    const glyphsTabIcon = getItemIcon('flame_glyph');

    return `
        <div class="skill-tabs">
            <button class="skill-tab ${currentTab === 'ichor' ? 'active' : ''}"
                    onclick="window.glyphbindingUI.switchTab('ichor')">
                <span class="tab-icon">💧</span>
                <span class="tab-name">Ichor</span>
            </button>
            <button class="skill-tab ${currentTab === 'glyphs' ? 'active' : ''}"
                    onclick="window.glyphbindingUI.switchTab('glyphs')">
                <span class="tab-glyph-icon silhouette">${glyphsTabIcon}</span>
                <span class="tab-name">Glyphs</span>
            </button>
            <button class="skill-tab ${currentTab === 'glyphCores' ? 'active' : ''}"
                    onclick="window.glyphbindingUI.switchTab('glyphCores')">
                <span class="tab-icon">🔮</span>
                <span class="tab-name">Glyph Cores</span>
            </button>
            <button class="skill-tab ${currentTab === 'focuses' ? 'active' : ''}"
                    onclick="window.glyphbindingUI.switchTab('focuses')">
                <span class="tab-icon">🌀</span>
                <span class="tab-name">Focuses</span>
            </button>
        </div>
    `;
}

// ============================================
//   ICHOR TAB - Node Cards
// ============================================

function renderNodeCard(node, playerLevel, currentAction) {
    const isLocked = playerLevel < node.level;
    const isActive = currentAction?.subType === 'ichor' && currentAction?.node?.id === node.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all bonuses
    const speedMultiplier = glyphbindingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeed = getCorruptionSpeedMultiplier(SKILLS.GLYPHBINDING);
    const corruptionXP = getCorruptionXPMultiplier(SKILLS.GLYPHBINDING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeed;
    const effectiveTime = node.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.GLYPHBINDING);
    let effectiveXP = node.xp;
    if (corruptionXP > 1) effectiveXP = Math.floor(effectiveXP * corruptionXP);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Loot multiplier for drops display (talent + corruption)
    const corruptionLoot = getCorruptionLootMultiplier(SKILLS.GLYPHBINDING);
    const talentLootBonus = getTalentLootBonus(SKILLS.GLYPHBINDING);
    const lootMult = (1 + talentLootBonus) * corruptionLoot;
    const drop = node.drops[0];
    const minQty = Math.max(1, Math.floor(drop.minQuantity * lootMult));
    const maxQty = Math.max(1, Math.floor(drop.maxQuantity * lootMult));
    const dropName = getItemName(drop.itemId);
    const dropIcon = getItemIcon(drop.itemId);

    return `
        <div class="node-card ${isLocked ? 'locked' : ''} ${isActive ? 'active' : ''}"
             data-node-id="${node.id}"
             style="--node-color: ${node.color}"
             onclick="${isLocked ? '' : `window.glyphbindingUI.toggleGatherIchor('${node.id}')`}">
            <div class="node-card-header">
                <span class="node-card-level">Lvl ${node.level}</span>
                <span class="node-icon">${dropIcon}</span>
                <span class="node-card-name">${node.name}</span>
            </div>
            <div class="node-card-info">
                <div class="node-stat">
                    <span class="stat-icon glyphbinding-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXP > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="node-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeed > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>
            <div class="node-drops">
                <div class="drop-info node-drop">
                    <span class="drop-icon">${dropIcon}</span>
                    <span class="drop-name">${dropName}</span>
                    <span class="drop-qty ${corruptionLoot > 1 ? 'boosted' : ''}">${minQty}-${maxQty}</span>
                </div>
            </div>
            <div class="node-action">
                ${isLocked ?
                    `<span class="locked-text">Requires Lvl ${node.level}</span>` :
                    `<button class="action-btn ${isActive ? 'active' : ''}">${isActive ? 'Stop' : 'Gather'}</button>`
                }
            </div>
            <div class="node-progress">
                <div class="node-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

function renderIchorTab() {
    const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
    const currentAction = gameState.getCurrentAction();
    const nodes = Object.values(ESSENCE_NODES);

    return `
        <div class="node-grid">
            ${nodes.map(node => renderNodeCard(node, skill.level, currentAction)).join('')}
        </div>
    `;
}

// ============================================
//   GLYPHS TAB - Glyph Cards
// ============================================

function renderGlyphCard(glyph, playerLevel, currentAction) {
    const isLocked = playerLevel < glyph.level;
    const isActive = currentAction?.subType === 'glyph' && currentAction?.glyph?.id === glyph.id;
    const progress = isActive ? currentAction.progress : 0;

    // Check for Universal Glyph Catalyst (permanent item)
    const hasCatalyst = gameState.getItemCount('universal_glyph_catalyst') >= 1;

    // Get ichor counts for all types
    const ichorCounts = {};
    for (const ichor of ALL_ICHOR_TYPES) {
        ichorCounts[ichor] = gameState.getItemCount(ichor);
    }

    // Determine which ichor to use:
    // 1. If player has selected an override for this glyph, use that
    // 2. Otherwise use the recipe's default ichor
    // 3. If catalyst active and no recipe ichor, find first available substitute
    let selectedIchor = selectedIchorOverrides[glyph.id] || glyph.inputItem;
    let selectedCount = ichorCounts[selectedIchor] || 0;

    // Check if selected ichor has enough quantity
    const hasSelectedIchor = selectedCount >= glyph.inputQuantity;

    // If catalyst active but selected ichor is insufficient, try to find a substitute
    if (hasCatalyst && !hasSelectedIchor) {
        // Try to find any ichor with enough
        for (const ichor of ALL_ICHOR_TYPES) {
            if (ichorCounts[ichor] >= glyph.inputQuantity) {
                selectedIchor = ichor;
                selectedCount = ichorCounts[ichor];
                break;
            }
        }
    }

    const hasMaterials = selectedCount >= glyph.inputQuantity;
    const isUsingNonDefault = selectedIchor !== glyph.inputItem;
    const materialName = getItemName(selectedIchor);
    const materialIcon = getItemIcon(selectedIchor);

    // Output glyph info (from drops array)
    const outputItemId = glyph.drops[0].itemId;
    const outputIcon = getItemIcon(outputItemId);
    const outputName = getItemName(outputItemId);

    // Calculate effective time with all bonuses (includes Arcane Efficiency for glyphs)
    let speedMultiplier = glyphbindingSkill.getEffectiveSpeedMultiplier();
    const glyphCraftSpeedBonus = getTalentFlatBonus(SKILLS.GLYPHBINDING, 'glyphCraftSpeed');
    if (glyphCraftSpeedBonus > 0) speedMultiplier *= (1 + glyphCraftSpeedBonus);
    const corruptionSpeed = getCorruptionSpeedMultiplier(SKILLS.GLYPHBINDING);
    const corruptionXP = getCorruptionXPMultiplier(SKILLS.GLYPHBINDING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeed;
    const effectiveTime = glyph.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.GLYPHBINDING);
    let effectiveXP = glyph.xp;
    if (corruptionXP > 1) effectiveXP = Math.floor(effectiveXP * corruptionXP);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Ashes bonus info (requires 4 ashes per craft)
    const ashesCount = gameState.getItemCount('ashes');
    const hasAshes = ashesCount >= 4;

    // Material display - completely different when catalyst is active
    let materialDisplay;
    if (hasCatalyst && !isLocked) {
        // Build ichor chip selector - all 3 options visible as clickable chips
        const chips = ALL_ICHOR_TYPES.map(ichor => {
            const count = ichorCounts[ichor];
            const icon = getItemIcon(ichor);
            const isSelected = ichor === selectedIchor;
            const hasEnough = count >= glyph.inputQuantity;
            const chipClass = `ichor-chip${isSelected ? ' selected' : ''}${!hasEnough ? ' insufficient' : ''}`;
            return `<button class="${chipClass}" data-ichor="${ichor}" onclick="event.stopPropagation(); window.glyphbindingUI.selectIchor('${glyph.id}', '${ichor}')">
                <span class="ichor-chip-icon">${icon}</span>
                <span class="ichor-chip-count">${count}</span>
            </button>`;
        }).join('');

        materialDisplay = `
            <div class="ichor-chips-row" onclick="event.stopPropagation();">
                ${chips}
            </div>
            <div class="ichor-need ${!hasMaterials ? 'missing' : ''}">Need: ${glyph.inputQuantity}</div>
        `;
    } else {
        // Normal material display without catalyst
        materialDisplay = `
            <div class="material-row ${!hasMaterials ? 'missing' : ''}">
                <span class="material-icon">${materialIcon}</span>
                <span class="material-name">${materialName}</span>
                <span class="material-count">${selectedCount}/${glyph.inputQuantity}</span>
            </div>
        `;
    }

    // Output quantity - matches completeCraftingGlyph: catalyst 2x, then (1 + talentLoot + glyphOutput) * corruption
    const corruptionLoot = getCorruptionLootMultiplier(SKILLS.GLYPHBINDING);
    const talentLootBonus = getTalentLootBonus(SKILLS.GLYPHBINDING);
    const glyphOutputBonus = getTalentFlatBonus(SKILLS.GLYPHBINDING, 'glyphOutputBonus');
    const baseOutput = hasCatalyst ? 2 : 1;
    const totalLootMult = 1 + talentLootBonus + glyphOutputBonus;
    const outputQty = Math.floor(baseOutput * totalLootMult * corruptionLoot);

    return `
        <div class="glyph-card ${isLocked ? 'locked' : ''} ${isActive ? 'active' : ''} ${!isLocked && !hasMaterials ? 'no-materials' : ''}"
             data-glyph-id="${glyph.id}"
             style="--glyph-color: ${glyph.color}"
             onclick="${isLocked ? '' : `window.glyphbindingUI.toggleCraftGlyph('${glyph.id}')`}">
            <div class="glyph-card-header">
                <span class="glyph-card-level">Lvl ${glyph.level}</span>
                <span class="glyph-icon">${outputIcon}</span>
                <span class="glyph-card-name">${glyph.name}</span>
            </div>
            <div class="glyph-card-info">
                <div class="glyph-stat">
                    <span class="stat-icon glyphbinding-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXP > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="glyph-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeed > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>
            <div class="glyph-materials">
                <div class="materials-label">Input</div>
                ${materialDisplay}
            </div>
            <div class="ashes-bonus ${hasAshes ? 'active' : 'inactive'}">
                <div class="ashes-bonus-header">
                    <span class="ashes-label">${getFireSpriteHTML()} Ashes Bonus</span>
                    <span class="ashes-info-icon" title="Auto-consumes 4 ashes per craft when available for 50% chance at extra output and XP">ⓘ</span>
                </div>
                <div class="ashes-bonus-content">
                    <span class="ashes-chance">50% extra output + XP</span>
                    <span class="ashes-count ${hasAshes ? '' : 'empty'}">${ashesCount}/4 ashes</span>
                </div>
            </div>
            <div class="glyph-output">
                <div class="output-label">Output</div>
                <div class="output-row">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    <span class="output-qty ${outputQty > 1 ? 'catalyst-boosted' : ''}">x${outputQty}</span>
                </div>
            </div>
            <div class="glyph-action">
                ${isLocked ?
                    `<span class="locked-text">Requires Lvl ${glyph.level}</span>` :
                    `<button class="action-btn ${isActive ? 'active' : ''}" ${!hasMaterials && !isActive ? 'disabled' : ''}>${isActive ? 'Stop' : 'Craft'}</button>`
                }
            </div>
            <div class="glyph-progress">
                <div class="glyph-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

function renderGlyphsTab() {
    const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
    const currentAction = gameState.getCurrentAction();
    const glyphs = Object.values(GLYPH_RECIPES);

    // Check for Universal Glyph Catalyst (permanent item, not consumed)
    const hasCatalyst = gameState.getItemCount('universal_glyph_catalyst') >= 1;

    return `
        ${hasCatalyst ? `
        <div class="catalyst-banner">
            <span class="catalyst-icon">🔮</span>
            <span class="catalyst-text">
                <strong>Universal Glyph Catalyst Active</strong>
                <span class="catalyst-effect">Any ichor works for any recipe • 2x glyph output</span>
            </span>
        </div>
        ` : ''}
        <div class="glyph-grid">
            ${glyphs.map(glyph => renderGlyphCard(glyph, skill.level, currentAction)).join('')}
        </div>
    `;
}

// ============================================
//   GLYPH CORES TAB - Glyph Core Cards
// ============================================

function renderGlyphCoreCard(recipe, playerLevel, currentAction) {
    const isLocked = playerLevel < recipe.level;
    const isActive = currentAction?.subType === 'glyphCore' && currentAction?.glyphCore?.id === recipe.id;
    const progress = isActive ? currentAction.progress : 0;

    // Check for Universal Glyph Catalyst
    const hasCatalyst = gameState.getItemCount('universal_glyph_catalyst') >= 1;

    // Find the ichor input in the recipe (if any)
    const ichorInput = recipe.inputItems.find(input => isIchorItem(input.itemId));

    // Get ichor counts for all types
    const ichorCounts = {};
    for (const ichor of ALL_ICHOR_TYPES) {
        ichorCounts[ichor] = gameState.getItemCount(ichor);
    }

    // Determine which ichor to use if catalyst active
    let selectedIchor = ichorInput ? (selectedIchorOverrides[recipe.id] || ichorInput.itemId) : null;
    let selectedIchorCount = selectedIchor ? (ichorCounts[selectedIchor] || 0) : 0;

    // If catalyst active and selected ichor insufficient, find substitute
    if (hasCatalyst && ichorInput && selectedIchorCount < ichorInput.quantity) {
        for (const ichor of ALL_ICHOR_TYPES) {
            if (ichorCounts[ichor] >= ichorInput.quantity) {
                selectedIchor = ichor;
                selectedIchorCount = ichorCounts[ichor];
                break;
            }
        }
    }

    // Check all materials (with ichor substitution if catalyst active)
    const materialChecks = recipe.inputItems.map(input => {
        const isIchor = isIchorItem(input.itemId);
        let playerHas, sufficient;

        if (isIchor && hasCatalyst) {
            playerHas = selectedIchorCount;
            sufficient = playerHas >= input.quantity;
        } else {
            playerHas = gameState.getItemCount(input.itemId);
            sufficient = playerHas >= input.quantity;
        }

        return {
            itemId: input.itemId,
            name: getItemName(isIchor && hasCatalyst ? selectedIchor : input.itemId),
            icon: getItemIcon(isIchor && hasCatalyst ? selectedIchor : input.itemId),
            required: input.quantity,
            has: playerHas,
            sufficient,
            isIchor
        };
    });
    const hasMaterials = materialChecks.every(m => m.sufficient);

    // Output item info
    const outputItemId = recipe.drops[0].itemId;
    const outputIcon = getItemIcon(outputItemId);
    const outputName = getItemName(outputItemId);

    // Calculate effective time with all bonuses
    const speedMultiplier = glyphbindingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeed = getCorruptionSpeedMultiplier(SKILLS.GLYPHBINDING);
    const corruptionXP = getCorruptionXPMultiplier(SKILLS.GLYPHBINDING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeed;
    const effectiveTime = recipe.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.GLYPHBINDING);
    let effectiveXP = recipe.xp;
    if (corruptionXP > 1) effectiveXP = Math.floor(effectiveXP * corruptionXP);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Build material rows - with dropdown for ichor when catalyst active
    const materialRows = materialChecks.map(mat => {
        if (mat.isIchor && hasCatalyst && !isLocked) {
            // Build ichor selector dropdown
            const options = ALL_ICHOR_TYPES.map(ichor => {
                const count = ichorCounts[ichor];
                const name = getItemName(ichor);
                const isSelected = ichor === selectedIchor;
                const hasEnough = count >= mat.required;
                return `<option value="${ichor}" ${isSelected ? 'selected' : ''} ${!hasEnough ? 'disabled' : ''}>${name} (${count})</option>`;
            }).join('');

            return `
                <div class="ichor-selector-wrapper" onclick="event.stopPropagation();">
                    <span class="ichor-selector-icon">${mat.icon}</span>
                    <select class="ichor-selector" onchange="window.glyphbindingUI.selectIchor('${recipe.id}', this.value);">
                        ${options}
                    </select>
                    <span class="ichor-selector-count ${!mat.sufficient ? 'missing' : ''}">${mat.has}/${mat.required}</span>
                </div>
            `;
        } else {
            return `
                <div class="material-row ${!mat.sufficient ? 'missing' : ''}">
                    <span class="material-icon">${mat.icon}</span>
                    <span class="material-name">${mat.name}</span>
                    <span class="material-count">${mat.has}/${mat.required}</span>
                </div>
            `;
        }
    }).join('');

    return `
        <div class="glyph-card ${isLocked ? 'locked' : ''} ${isActive ? 'active' : ''} ${!isLocked && !hasMaterials ? 'no-materials' : ''}"
             data-glyph-core-id="${recipe.id}"
             style="--glyph-color: ${recipe.color}"
             onclick="${isLocked ? '' : `window.glyphbindingUI.toggleCraftGlyphCore('${recipe.id}')`}">
            <div class="glyph-card-header">
                <span class="glyph-card-level">Lvl ${recipe.level}</span>
                <span class="glyph-icon">${outputIcon}</span>
                <span class="glyph-card-name">${recipe.name}</span>
            </div>
            <div class="glyph-card-info">
                <div class="glyph-stat">
                    <span class="stat-icon glyphbinding-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXP > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="glyph-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeed > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>
            <div class="glyph-materials">
                <span class="materials-label">Materials</span>
                ${materialRows}
            </div>
            <div class="glyph-output">
                <span class="output-label">Output</span>
                <div class="output-row">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    <span class="output-qty">x1</span>
                </div>
            </div>
            <div class="glyph-action">
                ${isLocked ?
                    `<span class="locked-text">Requires Lvl ${recipe.level}</span>` :
                    `<button class="action-btn ${isActive ? 'active' : ''}" ${!hasMaterials && !isActive ? 'disabled' : ''}>${isActive ? 'Stop' : 'Craft'}</button>`
                }
            </div>
            <div class="glyph-progress">
                <div class="glyph-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

function renderGlyphCoresTab() {
    const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
    const currentAction = gameState.getCurrentAction();
    const recipes = Object.values(GLYPH_CORE_RECIPES);

    // Check for Universal Glyph Catalyst
    const hasCatalyst = gameState.getItemCount('universal_glyph_catalyst') >= 1;

    return `
        ${hasCatalyst ? `
        <div class="catalyst-banner">
            <span class="catalyst-icon">🔮</span>
            <span class="catalyst-text">
                <strong>Universal Glyph Catalyst Active</strong>
                <span class="catalyst-effect">Any ichor works for ichor requirements</span>
            </span>
        </div>
        ` : ''}
        <div class="glyph-grid">
            ${recipes.map(recipe => renderGlyphCoreCard(recipe, skill.level, currentAction)).join('')}
        </div>
    `;
}

// ============================================
//   FOCUSES TAB - Focus Cards
// ============================================

function renderFocusCard(recipe, playerLevel, currentAction) {
    const isLocked = playerLevel < recipe.level;
    const isActive = currentAction?.subType === 'focus' && currentAction?.focus?.id === recipe.id;
    const progress = isActive ? currentAction.progress : 0;

    // Check for Universal Glyph Catalyst
    const hasCatalyst = gameState.getItemCount('universal_glyph_catalyst') >= 1;

    // Find the ichor input in the recipe (if any)
    const ichorInput = recipe.inputItems.find(input => isIchorItem(input.itemId));

    // Get ichor counts for all types
    const ichorCounts = {};
    for (const ichor of ALL_ICHOR_TYPES) {
        ichorCounts[ichor] = gameState.getItemCount(ichor);
    }

    // Determine which ichor to use if catalyst active
    let selectedIchor = ichorInput ? (selectedIchorOverrides[recipe.id] || ichorInput.itemId) : null;
    let selectedIchorCount = selectedIchor ? (ichorCounts[selectedIchor] || 0) : 0;

    // If catalyst active and selected ichor insufficient, find substitute
    if (hasCatalyst && ichorInput && selectedIchorCount < ichorInput.quantity) {
        for (const ichor of ALL_ICHOR_TYPES) {
            if (ichorCounts[ichor] >= ichorInput.quantity) {
                selectedIchor = ichor;
                selectedIchorCount = ichorCounts[ichor];
                break;
            }
        }
    }

    // Check all materials (with ichor substitution if catalyst active)
    const materialChecks = recipe.inputItems.map(input => {
        const isIchor = isIchorItem(input.itemId);
        let playerHas, sufficient;

        if (isIchor && hasCatalyst) {
            playerHas = selectedIchorCount;
            sufficient = playerHas >= input.quantity;
        } else {
            playerHas = gameState.getItemCount(input.itemId);
            sufficient = playerHas >= input.quantity;
        }

        return {
            itemId: input.itemId,
            name: getItemName(isIchor && hasCatalyst ? selectedIchor : input.itemId),
            icon: getItemIcon(isIchor && hasCatalyst ? selectedIchor : input.itemId),
            required: input.quantity,
            has: playerHas,
            sufficient,
            isIchor
        };
    });
    const hasMaterials = materialChecks.every(m => m.sufficient);

    // Output item info
    const outputItemId = recipe.drops[0].itemId;
    const outputIcon = getItemIcon(outputItemId);
    const outputName = getItemName(outputItemId);

    // Calculate effective time with all bonuses
    const speedMultiplier = glyphbindingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeed = getCorruptionSpeedMultiplier(SKILLS.GLYPHBINDING);
    const corruptionXP = getCorruptionXPMultiplier(SKILLS.GLYPHBINDING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeed;
    const effectiveTime = recipe.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.GLYPHBINDING);
    let effectiveXP = recipe.xp;
    if (corruptionXP > 1) effectiveXP = Math.floor(effectiveXP * corruptionXP);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Build material rows - with dropdown for ichor when catalyst active
    const materialRows = materialChecks.map(mat => {
        if (mat.isIchor && hasCatalyst && !isLocked) {
            // Build ichor selector dropdown
            const options = ALL_ICHOR_TYPES.map(ichor => {
                const count = ichorCounts[ichor];
                const name = getItemName(ichor);
                const isSelected = ichor === selectedIchor;
                const hasEnough = count >= mat.required;
                return `<option value="${ichor}" ${isSelected ? 'selected' : ''} ${!hasEnough ? 'disabled' : ''}>${name} (${count})</option>`;
            }).join('');

            return `
                <div class="ichor-selector-wrapper" onclick="event.stopPropagation();">
                    <span class="ichor-selector-icon">${mat.icon}</span>
                    <select class="ichor-selector" onchange="window.glyphbindingUI.selectIchor('${recipe.id}', this.value);">
                        ${options}
                    </select>
                    <span class="ichor-selector-count ${!mat.sufficient ? 'missing' : ''}">${mat.has}/${mat.required}</span>
                </div>
            `;
        } else {
            return `
                <div class="material-row ${!mat.sufficient ? 'missing' : ''}">
                    <span class="material-icon">${mat.icon}</span>
                    <span class="material-name">${mat.name}</span>
                    <span class="material-count">${mat.has}/${mat.required}</span>
                </div>
            `;
        }
    }).join('');

    return `
        <div class="glyph-card ${isLocked ? 'locked' : ''} ${isActive ? 'active' : ''} ${!isLocked && !hasMaterials ? 'no-materials' : ''}"
             data-focus-id="${recipe.id}"
             style="--glyph-color: ${recipe.color}"
             onclick="${isLocked ? '' : `window.glyphbindingUI.toggleCraftFocus('${recipe.id}')`}">
            <div class="glyph-card-header">
                <span class="glyph-card-level">Lvl ${recipe.level}</span>
                <span class="glyph-icon">${outputIcon}</span>
                <span class="glyph-card-name">${recipe.name}</span>
            </div>
            <div class="glyph-card-info">
                <div class="glyph-stat">
                    <span class="stat-icon glyphbinding-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXP > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="glyph-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeed > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>
            <div class="glyph-materials">
                <span class="materials-label">Materials</span>
                ${materialRows}
            </div>
            <div class="glyph-output">
                <span class="output-label">Output</span>
                <div class="output-row output-hoverable" data-tooltip="${buildEquipmentStatsTooltip(outputItemId)}">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    <span class="output-qty">x1</span>
                </div>
            </div>
            <div class="glyph-action">
                ${isLocked ?
                    `<span class="locked-text">Requires Lvl ${recipe.level}</span>` :
                    `<button class="action-btn ${isActive ? 'active' : ''}" ${!hasMaterials && !isActive ? 'disabled' : ''}>${isActive ? 'Stop' : 'Craft'}</button>`
                }
            </div>
            <div class="glyph-progress">
                <div class="glyph-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

function renderFocusesTab() {
    const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
    const currentAction = gameState.getCurrentAction();
    const recipes = Object.values(FOCUS_RECIPES);

    // Check for Universal Glyph Catalyst
    const hasCatalyst = gameState.getItemCount('universal_glyph_catalyst') >= 1;

    return `
        ${hasCatalyst ? `
        <div class="catalyst-banner">
            <span class="catalyst-icon">🔮</span>
            <span class="catalyst-text">
                <strong>Universal Glyph Catalyst Active</strong>
                <span class="catalyst-effect">Any ichor works for ichor requirements</span>
            </span>
        </div>
        ` : ''}
        <div class="glyph-grid">
            ${recipes.map(recipe => renderFocusCard(recipe, skill.level, currentAction)).join('')}
        </div>
    `;
}

// ============================================
//   MAIN RENDER/UPDATE/INIT
// ============================================

export function renderGlyphbindingUI(params = {}) {
    // Handle tab from route params
    if (params.tab) {
        currentTab = params.tab;
    }

    const container = document.getElementById('skill-content');
    let tabContent;
    if (currentTab === 'ichor') {
        tabContent = renderIchorTab();
    } else if (currentTab === 'glyphs') {
        tabContent = renderGlyphsTab();
    } else if (currentTab === 'focuses') {
        tabContent = renderFocusesTab();
    } else {
        tabContent = renderGlyphCoresTab();
    }

    container.innerHTML = `
        <div class="glyphbinding-interface">
            ${renderCompactHeader()}
            ${renderTabs()}
            <div class="glyphbinding-content">
                ${tabContent}
            </div>
        </div>
    `;
}

function updateMaterialCounts() {
    // Check for Universal Glyph Catalyst (permanent item)
    const hasCatalyst = gameState.getItemCount('universal_glyph_catalyst') >= 1;

    // Update glyph card material counts
    const glyphCards = document.querySelectorAll('.glyphbinding-interface .glyph-card[data-glyph-id]');

    glyphCards.forEach(card => {
        const glyphId = card.dataset.glyphId;
        if (!glyphId) return;

        const glyph = Object.values(GLYPH_RECIPES).find(g => g.id === glyphId);
        if (!glyph) return;

        // Determine which ichor to check based on catalyst state
        let selectedIchor = selectedIchorOverrides[glyphId] || glyph.inputItem;
        let playerHas = gameState.getItemCount(selectedIchor);

        // If catalyst active and selected ichor is insufficient, check if ANY ichor works
        if (hasCatalyst && playerHas < glyph.inputQuantity) {
            for (const ichor of ALL_ICHOR_TYPES) {
                const count = gameState.getItemCount(ichor);
                if (count >= glyph.inputQuantity) {
                    playerHas = count;
                    break;
                }
            }
        }

        const hasMaterials = playerHas >= glyph.inputQuantity;

        // Determine which ichor is actually displayed/selected in dropdown
        let displayedIchor = selectedIchorOverrides[glyphId] || glyph.inputItem;
        let displayedCount = gameState.getItemCount(displayedIchor);

        // If catalyst active and displayed ichor insufficient, find substitute (to match render logic)
        if (hasCatalyst && displayedCount < glyph.inputQuantity) {
            for (const ichor of ALL_ICHOR_TYPES) {
                const count = gameState.getItemCount(ichor);
                if (count >= glyph.inputQuantity) {
                    displayedIchor = ichor;
                    displayedCount = count;
                    break;
                }
            }
        }

        // Update count display - check for both ichor chips and material-count
        const chipsRow = card.querySelector('.ichor-chips-row');
        const countEl = card.querySelector('.material-count');
        if (chipsRow) {
            // Update each chip's count and state
            const chips = chipsRow.querySelectorAll('.ichor-chip');
            chips.forEach(chip => {
                const ichorId = chip.dataset.ichor;
                const count = gameState.getItemCount(ichorId);
                const hasEnough = count >= glyph.inputQuantity;
                const countSpan = chip.querySelector('.ichor-chip-count');
                if (countSpan && countSpan.textContent !== String(count)) {
                    countSpan.textContent = count;
                }
                chip.classList.toggle('insufficient', !hasEnough);
            });

            // Update need indicator
            const needEl = card.querySelector('.ichor-need');
            if (needEl) {
                needEl.classList.toggle('missing', displayedCount < glyph.inputQuantity);
            }
        } else if (countEl) {
            countEl.textContent = `${playerHas}/${glyph.inputQuantity}`;
        }

        // Update row styling (only for non-catalyst display)
        const materialRow = card.querySelector('.material-row');
        if (materialRow) {
            materialRow.classList.toggle('missing', !hasMaterials);
        }

        // Update card state (only if unlocked)
        if (!card.classList.contains('locked')) {
            card.classList.toggle('no-materials', !hasMaterials);
        }
    });

    // Update glyph core card material counts
    const glyphCoreCards = document.querySelectorAll('.glyphbinding-interface .glyph-card[data-glyph-core-id]');

    glyphCoreCards.forEach(card => {
        const glyphCoreId = card.dataset.glyphCoreId;
        if (!glyphCoreId) return;

        const recipe = Object.values(GLYPH_CORE_RECIPES).find(r => r.id === glyphCoreId);
        if (!recipe) return;

        // Find ichor input if any
        const ichorInput = recipe.inputItems.find(input => isIchorItem(input.itemId));

        // Determine ichor state for catalyst
        let selectedIchor = ichorInput ? (selectedIchorOverrides[glyphCoreId] || ichorInput.itemId) : null;
        let selectedIchorCount = selectedIchor ? gameState.getItemCount(selectedIchor) : 0;

        // If catalyst active and selected ichor insufficient, find substitute
        if (hasCatalyst && ichorInput && selectedIchorCount < ichorInput.quantity) {
            for (const ichor of ALL_ICHOR_TYPES) {
                const count = gameState.getItemCount(ichor);
                if (count >= ichorInput.quantity) {
                    selectedIchorCount = count;
                    break;
                }
            }
        }

        // Check all materials
        let hasMaterials = true;
        recipe.inputItems.forEach((input) => {
            const inputIsIchor = isIchorItem(input.itemId);
            let playerHas, sufficient;

            if (inputIsIchor && hasCatalyst) {
                playerHas = selectedIchorCount;
                sufficient = playerHas >= input.quantity;
            } else {
                playerHas = gameState.getItemCount(input.itemId);
                sufficient = playerHas >= input.quantity;
            }
            if (!sufficient) hasMaterials = false;
        });

        // Update ichor selector count if present
        const ichorCountEl = card.querySelector('.ichor-selector-count');
        if (ichorCountEl && ichorInput) {
            const selectedInDropdown = selectedIchorOverrides[glyphCoreId] || ichorInput.itemId;
            const selectedCount = gameState.getItemCount(selectedInDropdown);
            ichorCountEl.textContent = `${selectedCount}/${ichorInput.quantity}`;
            ichorCountEl.classList.toggle('missing', selectedCount < ichorInput.quantity);
        }

        // Update non-ichor material rows
        const materialRows = card.querySelectorAll('.material-row');
        let rowIndex = 0;
        recipe.inputItems.forEach((input) => {
            if (isIchorItem(input.itemId) && hasCatalyst) return; // Skip ichor when catalyst active

            const playerHas = gameState.getItemCount(input.itemId);
            const sufficient = playerHas >= input.quantity;

            const row = materialRows[rowIndex];
            if (row) {
                row.classList.toggle('missing', !sufficient);
                const countEl = row.querySelector('.material-count');
                if (countEl) countEl.textContent = `${playerHas}/${input.quantity}`;
            }
            rowIndex++;
        });

        // Update card state (only if unlocked)
        if (!card.classList.contains('locked')) {
            card.classList.toggle('no-materials', !hasMaterials);
        }
    });

    // Update focus card material counts
    const focusCards = document.querySelectorAll('.glyphbinding-interface .glyph-card[data-focus-id]');

    focusCards.forEach(card => {
        const focusId = card.dataset.focusId;
        if (!focusId) return;

        const recipe = Object.values(FOCUS_RECIPES).find(r => r.id === focusId);
        if (!recipe) return;

        // Find ichor input if any
        const ichorInput = recipe.inputItems.find(input => isIchorItem(input.itemId));

        // Determine ichor state for catalyst
        let selectedIchor = ichorInput ? (selectedIchorOverrides[focusId] || ichorInput.itemId) : null;
        let selectedIchorCount = selectedIchor ? gameState.getItemCount(selectedIchor) : 0;

        // If catalyst active and selected ichor insufficient, find substitute
        if (hasCatalyst && ichorInput && selectedIchorCount < ichorInput.quantity) {
            for (const ichor of ALL_ICHOR_TYPES) {
                const count = gameState.getItemCount(ichor);
                if (count >= ichorInput.quantity) {
                    selectedIchorCount = count;
                    break;
                }
            }
        }

        // Check all materials
        let hasMaterials = true;
        recipe.inputItems.forEach((input) => {
            const inputIsIchor = isIchorItem(input.itemId);
            let playerHas, sufficient;

            if (inputIsIchor && hasCatalyst) {
                playerHas = selectedIchorCount;
                sufficient = playerHas >= input.quantity;
            } else {
                playerHas = gameState.getItemCount(input.itemId);
                sufficient = playerHas >= input.quantity;
            }
            if (!sufficient) hasMaterials = false;
        });

        // Update ichor selector count if present
        const ichorCountEl = card.querySelector('.ichor-selector-count');
        if (ichorCountEl && ichorInput) {
            const selectedInDropdown = selectedIchorOverrides[focusId] || ichorInput.itemId;
            const selectedCount = gameState.getItemCount(selectedInDropdown);
            ichorCountEl.textContent = `${selectedCount}/${ichorInput.quantity}`;
            ichorCountEl.classList.toggle('missing', selectedCount < ichorInput.quantity);
        }

        // Update non-ichor material rows
        const materialRows = card.querySelectorAll('.material-row');
        let rowIndex = 0;
        recipe.inputItems.forEach((input) => {
            if (isIchorItem(input.itemId) && hasCatalyst) return; // Skip ichor when catalyst active

            const playerHas = gameState.getItemCount(input.itemId);
            const sufficient = playerHas >= input.quantity;

            const row = materialRows[rowIndex];
            if (row) {
                row.classList.toggle('missing', !sufficient);
                const countEl = row.querySelector('.material-count');
                if (countEl) countEl.textContent = `${playerHas}/${input.quantity}`;
            }
            rowIndex++;
        });

        // Update card state (only if unlocked)
        if (!card.classList.contains('locked')) {
            card.classList.toggle('no-materials', !hasMaterials);
        }
    });
}

export function updateGlyphbindingUI() {
    // Check if catalyst state changed (banked/withdrawn) - requires full re-render
    const currentCatalystState = gameState.getItemCount('universal_glyph_catalyst') >= 1;
    if (lastKnownCatalystState !== null && lastKnownCatalystState !== currentCatalystState) {
        lastKnownCatalystState = currentCatalystState;
        renderGlyphbindingUI();
        return;
    }
    lastKnownCatalystState = currentCatalystState;

    // Update material counts first
    updateMaterialCounts();

    // Update compact header stats
    const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
    const xpPerHour = getXPPerHour(SKILLS.GLYPHBINDING);
    const { levelDisplay } = getSkillProgressInfo(SKILLS.GLYPHBINDING);

    const xpTotalEl = document.querySelector('.glyphbinding-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    const xpRateEl = document.querySelector('.glyphbinding-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    updateCompactHeaderProgress('.glyphbinding-interface', SKILLS.GLYPHBINDING);

    // Update tool upgrade gold color
    updateToolUpgradeCost('.glyphbinding-interface');

    const currentAction = gameState.getCurrentAction();
    const isGlyphbindingAction = currentAction?.type === SKILLS.GLYPHBINDING;

    if (isGlyphbindingAction) {
        const progress = currentAction.progress;

        // Update active card progress
        let selector;
        if (currentAction.subType === 'ichor') {
            selector = `.node-card[data-node-id="${currentAction.node.id}"]`;
        } else if (currentAction.subType === 'glyph') {
            selector = `.glyph-card[data-glyph-id="${currentAction.glyph.id}"]`;
        } else if (currentAction.subType === 'glyphCore') {
            selector = `.glyph-card[data-glyph-core-id="${currentAction.glyphCore.id}"]`;
        } else if (currentAction.subType === 'focus') {
            selector = `.glyph-card[data-focus-id="${currentAction.focus.id}"]`;
        }

        if (selector) {
            const activeCard = document.querySelector(selector);
            if (activeCard) {
                const progressSelector = currentAction.subType === 'ichor' ? '.node-progress-fill' : '.glyph-progress-fill';
                const progressFill = activeCard.querySelector(progressSelector);
                if (progressFill) {
                    progressFill.style.width = `${progress * 100}%`;
                }
            }
        }
    } else {
        // No action - clear stale active states without full re-render
        document.querySelectorAll('.node-card.active, .glyph-card.active').forEach(card => {
            card.classList.remove('active');
            const fill = card.querySelector('.node-progress-fill, .glyph-progress-fill');
            if (fill) fill.style.width = '0%';
        });
    }
}

export function initGlyphbindingUI() {
    glyphbindingSkill.initializeStartingTool();

    // Shared completion handler — only full re-render on level-up (new cards unlocked)
    // Otherwise just update material counts in-place (updateGlyphbindingUI handles the rest)
    const onComplete = (item, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Glyphbinding level ${xpResult.newLevel}!`);
        }

        if (getCurrentSkillId() === SKILLS.GLYPHBINDING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                if (xpResult.leveledUp) {
                    renderGlyphbindingUI();
                } else {
                    updateMaterialCounts();
                }
            }
        }
    };

    glyphbindingSkill.onIchorComplete = onComplete;
    glyphbindingSkill.onGlyphComplete = onComplete;
    glyphbindingSkill.onGlyphCoreComplete = onComplete;
    glyphbindingSkill.onFocusComplete = onComplete;
}

// ============================================
//   PUBLIC API
// ============================================

window.glyphbindingUI = {
    toggleGatherIchor(nodeId) {
        const currentAction = gameState.getCurrentAction();
        if (currentAction?.type === SKILLS.GLYPHBINDING &&
            currentAction?.subType === 'ichor' &&
            currentAction?.node?.id === nodeId) {
            glyphbindingSkill.stopGatheringIchor();
        } else {
            const result = glyphbindingSkill.startGatheringIchor(nodeId);
            // Toast warnings handled in skill logic
        }
        renderGlyphbindingUI();
    },

    selectIchor(glyphId, ichorId) {
        // Store the selected ichor override for this glyph
        selectedIchorOverrides[glyphId] = ichorId;

        // Targeted update: toggle selected chip and update card state
        const card = document.querySelector(`.glyph-card[data-glyph-id="${glyphId}"]`);
        if (card) {
            const count = gameState.getItemCount(ichorId);
            const glyph = Object.values(GLYPH_RECIPES).find(g => g.id === glyphId);
            const needed = glyph?.inputQuantity || 1;

            // Update chip selection states
            const chips = card.querySelectorAll('.ichor-chip');
            chips.forEach(chip => {
                chip.classList.toggle('selected', chip.dataset.ichor === ichorId);
            });

            // Update need indicator
            const needEl = card.querySelector('.ichor-need');
            if (needEl) {
                needEl.classList.toggle('missing', count < needed);
            }

            card.classList.toggle('no-materials', count < needed && !card.classList.contains('locked'));
        } else {
            renderGlyphbindingUI();
        }
    },

    toggleCraftGlyph(glyphId) {
        const currentAction = gameState.getCurrentAction();
        if (currentAction?.type === SKILLS.GLYPHBINDING &&
            currentAction?.subType === 'glyph' &&
            currentAction?.glyph?.id === glyphId) {
            glyphbindingSkill.stopCraftingGlyph();
        } else {
            // Pass the selected ichor override if set
            const ichorOverride = selectedIchorOverrides[glyphId] || null;
            const result = glyphbindingSkill.startCraftingGlyph(glyphId, true, ichorOverride);
            // Toast warnings handled in skill logic
        }
        renderGlyphbindingUI();
    },

    toggleCraftGlyphCore(glyphCoreId) {
        const currentAction = gameState.getCurrentAction();
        if (currentAction?.type === SKILLS.GLYPHBINDING &&
            currentAction?.subType === 'glyphCore' &&
            currentAction?.glyphCore?.id === glyphCoreId) {
            glyphbindingSkill.stopCraftingGlyphCore();
        } else {
            // Pass the selected ichor override if set
            const ichorOverride = selectedIchorOverrides[glyphCoreId] || null;
            const result = glyphbindingSkill.startCraftingGlyphCore(glyphCoreId, true, ichorOverride);
            // Toast warnings handled in skill logic
        }
        renderGlyphbindingUI();
    },

    toggleCraftFocus(focusId) {
        const currentAction = gameState.getCurrentAction();
        if (currentAction?.type === SKILLS.GLYPHBINDING &&
            currentAction?.subType === 'focus' &&
            currentAction?.focus?.id === focusId) {
            glyphbindingSkill.stopCraftingFocus();
        } else {
            // Pass the selected ichor override if set
            const ichorOverride = selectedIchorOverrides[focusId] || null;
            const result = glyphbindingSkill.startCraftingFocus(focusId, true, ichorOverride);
            // Toast warnings handled in skill logic
        }
        renderGlyphbindingUI();
    },

    upgradeChisel(chiselId) {
        const result = glyphbindingSkill.upgradeChisel(chiselId);
        if (!result.success) {
            toastWarning(result.reason);
        }
        renderGlyphbindingUI();
    },

    switchTab(tabId) {
        currentTab = tabId;
        renderGlyphbindingUI();
    }
};
